// frontend/src/pages/LocationSetup.jsx

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useEditableGrid } from '../hooks/useEditableGrid';
import { dirtyCellPayload } from '../utils/dirtyCellPayload';
import { withSorReadOnly } from '../components/grid/sorColumns';
import { referencePickerColDef } from '../utils/referencePickerColDef';
import { formatLocationLabel } from '../utils/itemLabel';
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Alert,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Autocomplete,
  Typography,
} from "@mui/material";
import PageBanner from "../components/common/PageBanner";
import { ICON, CalendarBlank, Lock, PencilSimple, Plus, X } from '../icons';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import StatusChip from '../components/common/StatusChip';
import Tile from '../components/layout/Tile';
import CalendarEditor from '../components/calendar/CalendarEditor';
import { newCalendarPayload } from '../components/calendar/newCalendar';
import CalendarDaysView from '../components/calendar/CalendarDaysView';
import CalendarHolidaysSection from '../components/calendar/CalendarHolidaysSection';
import CalendarExceptionsSection from '../components/calendar/CalendarExceptionsSection';
import { sessionApi } from '../services/SessionApiService';
import { fetchAllPagesVia } from '../utils/fetchAllPages';
import { useAuth, useIsShopify } from '../contexts/AuthContext';
import { defaultCountryFromStoreRegion } from '../utils/country';
import { useAgGridTheme } from '../utils/agGridTheme';
import GridIconAction from '../components/grid/GridIconAction';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, iconActionColumn } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import { useCountries, useLocationTypes, useCalendars } from '../hooks/useReferenceData';
import { useWriteGate } from '../hooks/useWriteGate';
import { useCreateCalendar, useUpdateCalendar } from '../hooks/useCalendarData';
import { useCreateLocation, useUpdateLocation, useLocationBulkUpdate } from '../hooks/useLocationData';
import { translateRequestError } from '../queries/api';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSsrmGrid } from '../hooks/useSsrmGrid';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SaveDiscardButtons from '../components/common/SaveDiscardButtons';
import GridExportButton from '../components/common/GridExportButton';
import FilterRow from '../components/common/FilterRow';
import SearchField from '../components/common/SearchField';
import '../styles/pages.css';
import { lengthLimits } from '../constants/orgFieldLimits';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// Mirrors api.models.Location.LocationCode (see lengthLimits).
const ADD_LOCATION_FIELD_MAX = { code: 30 };
// The two picker columns render LocationSerializer's read-only nested object;
// the serializer writes them through the `_id` input the picker sets beside it.
const LOCATION_WIRE_FIELDS = {
  location_type: 'location_type_id',
  location_country_code: 'location_country_code_id',
};

function LocationSetup() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const isShopify = useIsShopify();
  // While an ERP owns location master the backend refuses a create here
  // (location_create_erp_managed): the merchant creates the location in the
  // ERP and adds it through the connector's location review.
  const canCreateLocation = useWriteGate('locations').mayWrite;
  const { confirm, dialogProps } = useConfirmDialog();
  const { data: countries, error: countriesError } = useCountries();
  const { data: locationTypes, error: locationTypesError } = useLocationTypes();
  const { data: calendars, error: calendarsError } = useCalendars();
  // Surface a single banner if any reference dataset failed to load.
  // Detail tracking sits on each individual hook's `error` for diagnostics.
  const refDataError = (countriesError || locationTypesError || calendarsError)
    ? t('common.refDataFailed')
    : null;
  const [, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');

  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const gridState = useGridState('location_setup');
  const pageSize = 100;

  // Add Location dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', code: '', type: null, calendar: null, address1: '', address2: '', city: '', stateProvince: '', postalCode: '', country: null });

  // Edit Location dialog state. Triggered by the edit icon in the
  // row's Actions column. Edits the location's name + full address;
  // address fields are required only on Shopify stores (parity with
  // the Add Location dialog). PATCHes only the dialog's fields —
  // inline grid edits for the same row stay queued for the bulk Save
  // Changes button (the two flows are independent).
  //
  // Declared above createColumnDefs so the column-defs deps array can
  // reference openEditDialog without a TDZ error.
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, name: '', address1: '', address2: '', city: '', stateProvince: '', postalCode: '', country: null });

  const openEditDialog = useCallback((location) => {
    setEditForm({
      id: location.location_id,
      name: location.location_name || '',
      address1: location.location_address_line1 || '',
      address2: location.location_address_line2 || '',
      city: location.location_city || '',
      stateProvince: location.location_state_province || '',
      postalCode: location.location_postal_code || '',
      country: location.location_country_code?.country_code
        || defaultCountryFromStoreRegion(countries, selectedStore?.region_code),
    });
    setEditDialogOpen(true);
  }, [countries, selectedStore?.region_code]);

  // Mutations — pending state replaces local addLoading/editLoading.
  const createCalendarMutation = useCreateCalendar();
  const updateCalendarMutation = useUpdateCalendar();
  const createLocationMutation = useCreateLocation();
  const updateLocationMutation = useUpdateLocation();
  const bulkUpdateMutation = useLocationBulkUpdate();
  const addLoading = createLocationMutation.isPending;
  // Edit dialog Save uses the same updateLocationMutation as the
  // calendar-assignment / toggle flows, but its isPending state is only
  // meaningful while the dialog is open. The dialog Save button reads
  // this directly.
  const editLoading = updateLocationMutation.isPending && editDialogOpen;

  // Calendar modal state
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [modalCalendar, setModalCalendar] = useState(null);
  const [modalLocation, setModalLocation] = useState(null);
  const [allLocations, setAllLocations] = useState([]);
  const [calendarStatus, setCalendarStatus] = useState({ dirty: false, canSave: false });
  const [modalCalendarName, setModalCalendarName] = useState('');
  const [previewPattern, setPreviewPattern] = useState(null);
  const calendarEditorRef = useRef(null);
  // Anchors for the in-modal "Go to Working Days" / "Go to Recurring
  // Holidays" links inside the calendar grid popover.
  const workingDaysSectionRef = useRef(null);
  const holidaysSectionRef = useRef(null);

  // Owned here, passed to useSsrmGrid which assigns event.api in its
  // onGridReady. Keeping the ref local lets callbacks declared above the
  // hook call (handleSsrmData) read it
  // without ordering games or destructure-rename lint flags.
  const gridApiRef = useRef(null);

  // Bulk-save scaffolding (dirty cells, save, discard, keyboard-save,
  // nav guard) lives in useEditableGrid. This page keeps a custom
  // onCellValueChanged below because deactivating a Shopify location
  // needs a confirm interstitial; the hook's `recordCellChange` primitive
  // handles the dirty bookkeeping.
  const editing = useEditableGrid({
    // Arrow indirection: `refresh` comes from useSsrmGrid below, and this is
    // only ever invoked after a save — never during render.
    refresh: (opts) => refresh(opts),
    getRowId: (row) => row.location_id,
    mutation: bulkUpdateMutation,
    buildPayload: (_rows, changedCells) => dirtyCellPayload(changedCells, LOCATION_WIRE_FIELDS),
    successKey: 'locationSetup.messages.saveSuccess',
    failureKey: 'locationSetup.messages.saveFailed',
  });
  const { gridContext } = editing;

  const exportProps = {
    endpoint: '/api/locations/export/',
    filename: 'locations',
    getParams: useCallback(
      (scope) => (scope !== 'all' && searchText) ? `&search=${encodeURIComponent(searchText)}` : '',
      [searchText]
    ),
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchAllLocations = useCallback(async () => {
    try {
      setAllLocations(await fetchAllPagesVia(
        sessionApi, '/api/locations/', { label: 'locations' },
      ));
    } catch (error) {
      console.error("Error fetching locations:", error);
      showToast(t('common.loadError'), 'error');
    }
  }, [t]);

  const openCalendarModal = useCallback((location) => {
    const calendar = location.calendar || null;
    setModalLocation(location);
    setModalCalendar(calendar);
    setModalCalendarName(calendar?.calendar_name || '');
    setAllLocations([]);
    setCalendarModalOpen(true);
    fetchAllLocations();
  }, [fetchAllLocations]);

  // ── Column definitions ──────────────────────────────────────────────
  const createColumnDefs = useCallback(() => {
    // Location master (Domain.LOCATIONS) is read-only when the ERP is its SoR
    // (edited in the ERP) — mirrors backend SoRReadOnlyMixin. Applied via the
    // post-process below; the 3PL/fulfillment-service read-only rules are
    // separate (channel-managed).
    const SOR_FIELDS = {
      location_name: 'locations', location_code: 'locations',
      location_type: 'locations', location_country_code: 'locations',
      location_city: 'locations', location_address_line1: 'locations',
      location_address_line2: 'locations', location_state_province: 'locations',
      location_postal_code: 'locations',
    };
    // Fulfillment-service locations are read-only — Shopify only allows
    // the owning fulfillment service app to edit them.
    const isEditable = (params) => !params.data.is_fulfillment_service;
    const isEditableNonShopify = (params) => !isShopify && isEditable(params);

    const _cols = [
      // --- Core Identity ---
      {
        field: "location_name",
        headerName: t("locationSetup.columns.name"),
        editable: isEditable,
        cellRenderer: (params) => {
          const isFulfillment = params.data.is_fulfillment_service;
          if (!isFulfillment) return params.value;

          // Use service name only when it differs from the location name,
          // otherwise it's redundant (Shopify initializes both to the same value).
          const rawServiceName = params.data.fulfillment_service_name || params.data.fulfillment_service_handle || '';
          const isDuplicate = rawServiceName && rawServiceName === params.data.location_name;
          const chipLabel = isDuplicate ? t("locationSetup.externallyManaged") : (rawServiceName || t("locationSetup.externallyManaged"));
          const tooltipName = isDuplicate ? t("locationSetup.externalFulfillmentService") : rawServiceName;

          return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{params.value}</span>
              <Tooltip
                title={t("locationSetup.tooltips.managedByService", { name: tooltipName })}
              >
                <StatusChip
                  icon={<Lock size={ICON.xs} />}
                  label={chipLabel}
                  color="neutral"
                />
              </Tooltip>
            </span>
          );
        },
      },
      // On a Shopify store LocationCode is the location GID's numeric suffix
      // (shopify_app/services/sync.py sync_location) — a number that appears
      // nowhere in the merchant's Shopify admin, so there is nothing to show
      // and nothing to edit. The add-location dialog already omits the field
      // for the same reason. Elsewhere the code IS the merchant's own.
      ...(!isShopify ? [{
        field: "location_code",
        headerName: t("locationSetup.columns.code"),
        editable: isEditable,
      }] : []),
      {
        ...referencePickerColDef({
          field: 'location_type',
          colId: 'LocationTypeID__LocationTypeName',
          options: locationTypes,
          optionId: 'location_type_id',
          formatLabel: (dt) => dt.location_type_name,
          // LocationSerializer refuses a location without a type.
          required: true,
        }),
        headerName: t("locationSetup.columns.type"),
        headerTooltip: t("locationSetup.tooltips.type"),
        editable: isEditableNonShopify,
      },

      // --- Location ---
      {
        field: "location_city",
        headerName: t("locationSetup.columns.city"),
        editable: isEditable,
      },
      {
        ...referencePickerColDef({
          field: 'location_country_code',
          colId: 'LocationCountryCode__name',
          options: countries,
          optionId: 'country_code',
          formatLabel: (c) => c.country_name,
          // Country is searchable by both name and ISO code \u2014 typing
          // "PL" or "Poland" should both match.
          searchableLabels: (c) => [c.country_name, c.country_code],
        }),
        headerName: t("locationSetup.columns.country"),
        editable: isEditable,
      },

      // --- Calendar (click or Enter opens the editor modal) ---
      {
        field: "calendar",
        colId: "CalendarID__CalendarName",
        headerName: t("locationSetup.columns.calendar"),
        headerTooltip: t("locationSetup.tooltips.calendar"),
        valueFormatter: (params) => {
          return params.data.calendar?.calendar_name || '\u2014';
        },
        // Nothing is typed here — the editor is a modal. Said out loud rather
        // than left unset, because the double-click handler explains away
        // locked EDITABLE cells and reads `colDef.editable` to decide; unset,
        // it would mistake this cell for one.
        editable: false,
        // `.ag-cell-opens-dialog` lives in styles/pages.css, as does the
        // ellipsis the button draws.
        cellClass: 'ag-cell-opens-dialog',
        // A real button, the same control Item setup's Default supplier uses:
        // it answers the keyboard as well as the mouse, and the ellipsis — the
        // part that says "click me" — as well as the text.
        cellRenderer: (params) => (
          <button
            type="button"
            className="cell-dialog-button"
            // With no calendar the label is an em dash, which names nothing
            // to a screen reader. The column's own header does.
            aria-label={params.data.calendar ? undefined : t('locationSetup.columns.calendar')}
            onClick={() => openCalendarModal(params.data)}
          >
            <span className="cell-dialog-button__label">{params.valueFormatted}</span>
          </button>
        ),
      },

      // --- Flags ---
      {
        field: "is_active",
        headerName: t("locationSetup.columns.active"),
        type: 'booleanColumn',
        editable: isEditable,
      },
      {
        field: "fulfills_online_orders",
        headerName: t("locationSetup.columns.fulfillsOrders"),
        type: 'booleanColumn',
        editable: isEditable,
      },
      {
        field: "data_source",
        headerName: t("locationSetup.columns.source"),
        valueFormatter: (params) => params.value || '\u2014',
      },

      // --- Address (hidden by default — user can show via column tray) ---
      {
        field: "location_address_line1",
        headerName: t("locationSetup.columns.address1"),
        editable: isEditable,
      },
      {
        field: "location_address_line2",
        headerName: t("locationSetup.columns.address2"),
        editable: isEditable,
      },
      {
        field: "location_state_province",
        headerName: t("locationSetup.columns.stateProvince"),
        editable: isEditable,
      },
      {
        field: "location_postal_code",
        headerName: t("locationSetup.columns.postalCode"),
        editable: isEditable,
      },

      // --- 3PL (hidden by default) ---
      {
        field: "is_fulfillment_service",
        headerName: t("locationSetup.columns.fulfillmentService"),
        headerTooltip: t("locationSetup.tooltips.fulfillmentService"),
        type: 'booleanColumn',
        // Read-only on every store: the sales channel's sync is the only
        // writer of the 3PL fields (LocationSerializer).
        editable: false,
      },
      {
        field: "fulfillment_service_handle",
        headerName: t("locationSetup.columns.fulfillmentServiceHandle"),
        valueFormatter: (params) => params.value || '\u2014',
      },

      // --- Actions (Edit dialog trigger) ---
      // Read-only column \u2014 never participates in sorting, editing,
      // filtering, or export (iconActionColumn's flags; pinned by
      // gridDefaults.test.js).
      iconActionColumn({
        colId: "_actions",
        cellRenderer: GridIconAction,
        cellRendererParams: {
          icon: PencilSimple,
          label: t("btn.edit"),
          onClick: openEditDialog,
        },
      }),
    ];
    return _cols.map((c) => {
      const domain = SOR_FIELDS[c.field];
      return domain ? withSorReadOnly(c, domain, t) : c;
    });
  }, [countries, locationTypes, isShopify, t, openEditDialog, openCalendarModal]);

  // Column defs rebuild whenever createColumnDefs's deps change (incl. language via t).
  const columnDefs = useMemo(() => createColumnDefs(), [createColumnDefs]);



  // ── SSRM Datasource ────────────────────────────────────────────────
  const ssrmParams = useMemo(() => ({ search: searchText }), [searchText]);

  // Coerce three boolean-flag fields that the backend serialises as 0/1 —
  // the grid's checkbox cells need real booleans.
  const transformLocationRows = useCallback((data) => {
    return data.results.map(loc => ({
      ...loc,
      is_active: !!loc.is_active,
      fulfills_online_orders: !!loc.fulfills_online_orders,
      is_fulfillment_service: !!loc.is_fulfillment_service,
    }));
  }, []);

  const handleSsrmData = useCallback((data) => {
    setTotalCount(data.count || 0);
  }, []);

  // Stable identity: getRowId is an AG Grid initial property, so a new
  // function on a later render cannot be applied (warning #22).
  const getRowId = useCallback((params) => String(params.data.location_id), []);

  const { onGridReady, onGridPreDestroyed, refresh, pagination } = useSsrmGrid({
    endpoint: '/api/locations/',
    params: ssrmParams,
    pageSize,
    transformRows: transformLocationRows,
    onData: handleSsrmData,
    onError: (err) => showToast(translateRequestError(err, t, 'common.fetchError'), 'error'),
    gridRef: gridApiRef,
  });

  // ── Search ──────────────────────────────────────────────────────────
  const handleSearchChange = async (value) => {
    if (editing.isDirty) {
      const ok = await confirm({
        title: t('common.unsavedChangesTitle'),
        message: t('locationSetup.messages.unsavedChangesFilter'),
        confirmLabel: t('common.discard'),
        variant: 'warning',
      });
      if (!ok) return;
      editing.clearAll();
    }
    setSearchInput(value);
  };

  // ── Cell editing ────────────────────────────────────────────────────
  // Custom handler because deactivating a Shopify location needs a confirm
  // interstitial that reverts on cancel.
  const onCellValueChanged = useCallback(async (event) => {
    const updatedLocation = event.data;
    const fieldName = event.colDef.field;
    const oldValue = event.oldValue;
    const newValue = event.newValue;

    // Confirm location deactivation for Shopify stores
    if (isShopify && fieldName === 'is_active' && oldValue === true && newValue === false) {
      const ok = await confirm({
        title: t('locationSetup.messages.deactivateLocationTitle'),
        message: t('locationSetup.messages.deactivateLocationMessage', { name: updatedLocation.location_name || '' }),
        confirmLabel: t('common.confirm'),
        variant: 'warning',
      });
      if (!ok) {
        // Revert the value
        updatedLocation.is_active = true;
        event.api.refreshCells({ force: true, rowNodes: [event.node], columns: ['is_active'] });
        return;
      }
    }

    editing.recordCellChange({
      row: updatedLocation,
      fieldName,
      oldValue,
      newValue,
    });

    if (event.api) {
      event.api.refreshCells({
        force: true,
        rowNodes: [event.node],
        columns: [fieldName],
      });
    }
  }, [isShopify, confirm, t, editing]);

  // ── Add new location ────────────────────────────────────────────────
  const generateCode = (name) => {
    if (!name || !name.trim()) return '';
    return name.trim()
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9]/g, '_')
      .replace(/_+/g, '_').replace(/^_|_$/g, '')
      .toUpperCase()
      .slice(0, 30);
  };

  const handleCreateCalendarForAdd = async () => {
    try {
      const newCalendar = await createCalendarMutation.mutateAsync(newCalendarPayload(addForm.name, t));
      setAddForm(prev => ({ ...prev, calendar: newCalendar.calendar_id }));
      showToast(t("locationSetup.messages.calendarCreated"), "success");
    } catch (error) {
      console.error("createCalendarForAdd:", error.message);
      showToast(translateRequestError(error, t, 'locationSetup.messages.calendarFailed'), "error");
    }
  };

  const openAddDialog = () => {
    const defaultType = locationTypes.length > 0 ? locationTypes[0].location_type_id : null;
    const defaultCalendar = calendars.length > 0 ? calendars[0].calendar_id : null;
    setAddForm({ name: '', code: '', type: defaultType, calendar: defaultCalendar, address1: '', address2: '', city: '', stateProvince: '', postalCode: '', country: defaultCountryFromStoreRegion(countries, selectedStore?.region_code) });
    setAddDialogOpen(true);
  };

  const addLengths = lengthLimits(addForm, ADD_LOCATION_FIELD_MAX, t);

  const handleAddLocation = async () => {
    if (!addForm.name.trim()) return;
    if (isShopify && (!addForm.address1.trim() || !addForm.city.trim() || !addForm.postalCode.trim() || !addForm.country)) {
      showToast(t("locationSetup.messages.addressRequired"), "warning");
      return;
    }
    try {
      await createLocationMutation.mutateAsync({
        location_name: addForm.name.trim(),
        location_code: addForm.code || generateCode(addForm.name),
        location_type_id: addForm.type,
        calendar_id: addForm.calendar,
        location_address_line1: addForm.address1.trim(),
        location_address_line2: addForm.address2.trim(),
        location_city: addForm.city.trim(),
        location_state_province: addForm.stateProvince.trim(),
        location_postal_code: addForm.postalCode.trim(),
        location_country_code_id: addForm.country,
        is_active: true,
        fulfills_online_orders: true,
      });
      showToast(t("locationSetup.messages.addSuccess"), "success");
      setAddDialogOpen(false);
    } catch (error) {
      console.error("addLocation:", error.message);
      showToast(translateRequestError(error, t, 'locationSetup.messages.addFailed'), "error");
    }
  };

  const handleEditLocation = async () => {
    if (isShopify && (!editForm.address1.trim() || !editForm.city.trim() || !editForm.postalCode.trim() || !editForm.country)) {
      showToast(t("locationSetup.messages.addressRequired"), "warning");
      return;
    }
    const payload = {
      location_name: editForm.name,
      location_address_line1: editForm.address1.trim(),
      location_address_line2: editForm.address2.trim(),
      location_city: editForm.city.trim(),
      location_state_province: editForm.stateProvince.trim(),
      location_postal_code: editForm.postalCode.trim(),
      location_country_code_id: editForm.country,
    };

    try {
      await updateLocationMutation.mutateAsync({ id: editForm.id, payload });
      showToast(t("locationSetup.messages.saveSuccess", { count: 1 }), "success");
      setEditDialogOpen(false);
    } catch (error) {
      console.error("editLocation:", error.message);
      showToast(translateRequestError(error, t, 'locationSetup.messages.saveFailed'), "error");
    }
  };

  // ── Calendar modal ──────────────────────────────────────────────────
  // Every location, across as many pages as it takes, and a failed read is
  // surfaced: this list drives the calendar modal's "apply to these
  // locations" set, and a missing location there is a location that silently
  // keeps the wrong calendar.
  const closeCalendarModal = () => {
    setCalendarModalOpen(false);
    setModalCalendar(null);
    setModalLocation(null);
    setAllLocations([]);
  };


  // Show feedback when user double-clicks a read-only fulfillment service cell
  const onCellDoubleClicked = useCallback((event) => {
    if (event.data?.is_fulfillment_service && event.colDef.editable !== false) {
      const rawName = event.data.fulfillment_service_name || event.data.fulfillment_service_handle || '';
      const isDuplicate = rawName && rawName === event.data.location_name;
      const displayName = isDuplicate ? t("locationSetup.externalFulfillmentService") : rawName;
      showToast(
        t("locationSetup.messages.fulfillmentServiceReadOnly", { name: displayName }),
        "info"
      );
    }
  }, [t]);

  const handleCalendarUpdate = async (updatedCalendar) => {
    try {
      const saved = await updateCalendarMutation.mutateAsync({
        id: updatedCalendar.calendar_id,
        payload: updatedCalendar,
      });
      setModalCalendar(saved);
      setModalCalendarName(saved.calendar_name || '');
      showToast(t("locationSetup.messages.calendarUpdated"), "success");
    } catch (error) {
      console.error("calendarUpdate:", error.message);
      showToast(translateRequestError(error, t, 'locationSetup.messages.calendarFailed'), "error");
    }
  };

  const handleCreateCalendar = async () => {
    if (!modalLocation) return;
    try {
      const newCalendar = await createCalendarMutation.mutateAsync(
        newCalendarPayload(modalLocation.location_name, t),
      );

      await updateLocationMutation.mutateAsync({
        id: modalLocation.location_id,
        payload: { calendar_id: newCalendar.calendar_id },
      });

      setModalCalendar(newCalendar);
      setModalCalendarName(newCalendar.calendar_name || '');
      // Optimistic local update — keep the nested `calendar` wire field in
      // sync.
      setModalLocation(prev => prev ? { ...prev, calendar: newCalendar } : null);
      fetchAllLocations();
      showToast(t("locationSetup.messages.calendarCreated"), "success");
    } catch (error) {
      console.error("createCalendar:", error.message);
      showToast(translateRequestError(error, t, 'locationSetup.messages.calendarFailed'), "error");
    }
  };

  const handleSelectCalendar = async (calendar) => {
    if (!modalLocation || !calendar) return;
    // The autocomplete is an *assignment* control, not navigation — picking a
    // different calendar moves this location to it. Confirm so merchants don't
    // reassign by accident when they meant to browse.
    const ok = await confirm({
      title: t('locationSetup.calendar.assignConfirmTitle'),
      message: t('locationSetup.calendar.assignConfirmMessage', {
        location: modalLocation.location_name,
        calendar: calendar.calendar_name,
      }),
      confirmLabel: t('locationSetup.calendar.assignConfirmAction'),
      variant: 'info',
    });
    if (!ok) return;

    try {
      await updateLocationMutation.mutateAsync({
        id: modalLocation.location_id,
        payload: { calendar_id: calendar.calendar_id },
      });
      setModalCalendar(calendar);
      setModalCalendarName(calendar.calendar_name || '');
      setModalLocation(prev => prev ? { ...prev, calendar } : null);
      fetchAllLocations();
    } catch (error) {
      console.error("selectCalendar:", error.message);
      showToast(translateRequestError(error, t, 'locationSetup.messages.calendarFailed'), "error");
    }
  };

  const handleToggleLocation = async (locationId, isCurrentlyAssigned) => {
    try {
      await updateLocationMutation.mutateAsync({
        id: locationId,
        payload: isCurrentlyAssigned
          ? { calendar_id: null }
          : { calendar_id: modalCalendar.calendar_id },
      });
      fetchAllLocations();
    } catch (error) {
      console.error("toggleLocationCalendar:", error.message);
      showToast(translateRequestError(error, t, 'locationSetup.messages.calendarFailed'), "error");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {editing.unsavedGuard}
      <PageBanner severity="warning" messages={[refDataError]} />
      {/* ── Location Grid ── */}
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.locations')}
          editActions={
            <SaveDiscardButtons
              count={editing.changedRows.length}
              saving={editing.isSaving}
              onSave={editing.save}
              onDiscard={editing.discard}
            />
          }
          headerActions={(
            <>
              {canCreateLocation && (
                <Button variant="contained" startIcon={<Plus size={ICON.md} />} onClick={openAddDialog}>
                  {t("locationSetup.addLocation")}
                </Button>
              )}
              <GridExportButton {...exportProps} />
            </>
          )}
        >
          <GridPage>
            <FilterRow>
              <SearchField
                className="filter-row-search"
                label={t("locationSetup.searchLabel")}
                placeholder={t("locationSetup.searchPlaceholder")}
                value={searchInput}
                onChange={handleSearchChange}
              />
            </FilterRow>

            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={!!searchText}
                >
                  <AgGridReact
                    {...defaultGridProps}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    {...editableGridProps}
                    context={gridContext}
                    theme={gridTheme}
                    {...gridState}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={defaultColDef}
                    columnTypes={columnTypes}
                    onCellValueChanged={onCellValueChanged}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    onCellDoubleClicked={onCellDoubleClicked}
                    rowClassRules={{
                      'ag-row-fulfillment-service': (params) => !!params.data?.is_fulfillment_service,
                    }}
                    rowModelType="serverSide"
                    serverSideSortAllLevels={true}
                    {...pagination}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>

      {/* ── Add Location Dialog ── */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} className="dialog-form">
        <DialogTitle>
          {t("locationSetup.addLocation")}
          <IconButton onClick={() => setAddDialogOpen(false)} size="small"><X size={ICON.xl} /></IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Section: General */}
          <Typography className="dialog-form-section">{t("locationSetup.sections.general")}</Typography>
          <Box className="form-row-2col">
            <TextField
              label={t("locationSetup.columns.name")}
              value={addForm.name}
              onChange={(e) => {
                const name = e.target.value;
                setAddForm(prev => ({
                  ...prev,
                  name,
                  code: prev.code === generateCode(prev.name) || !prev.code
                    ? generateCode(name)
                    : prev.code,
                }));
              }}
              size="small"
              autoFocus
              required
              className="flex-1"
            />
            {!isShopify && (
              <TextField
                label={t("locationSetup.columns.code")}
                value={addForm.code}
                onChange={(e) => setAddForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                size="small"
                className="flex-half"
                {...addLengths.propsFor('code')}
              />
            )}
          </Box>
          <Box className="form-row-2col">
            <Autocomplete
              options={locationTypes}
              value={locationTypes.find(dt => dt.location_type_id === addForm.type) || null}
              getOptionLabel={(o) => o.location_type_name || ''}
              onChange={(_, v) => setAddForm(prev => ({ ...prev, type: v?.location_type_id || null }))}
              renderInput={(params) => <TextField {...params} label={t("locationSetup.columns.type")} size="small" />}
              size="small"
              className="flex-1"
            />
            <Autocomplete
              options={[{ calendar_id: '__NEW__', calendar_name: t("locationSetup.calendar.createNew") }, ...calendars]}
              value={calendars.find(c => c.calendar_id === addForm.calendar) || null}
              getOptionLabel={(o) => o.calendar_name || ''}
              isOptionEqualToValue={(o, v) => o.calendar_id === v.calendar_id}
              onChange={(_, v) => {
                if (v?.calendar_id === '__NEW__') {
                  handleCreateCalendarForAdd();
                } else {
                  setAddForm(prev => ({ ...prev, calendar: v?.calendar_id || null }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("locationSetup.columns.calendar")}
                  size="small"
                  required
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.calendar_id}>
                  {option.calendar_id === '__NEW__' ? (
                    <Typography color="primary" variant="body2">+ {option.calendar_name}</Typography>
                  ) : option.calendar_name}
                </li>
              )}
              size="small"
              className="flex-1"
            />
          </Box>

          {/* Section: Address */}
          <Typography className="dialog-form-section">{t("locationSetup.sections.address")}</Typography>
          <Box className="form-row-2col">
            <TextField
              label={t("locationSetup.columns.address1")}
              value={addForm.address1}
              onChange={(e) => setAddForm(prev => ({ ...prev, address1: e.target.value }))}
              size="small"
              required={isShopify}
              className="flex-1"
            />
            <TextField
              label={t("locationSetup.columns.city")}
              value={addForm.city}
              onChange={(e) => setAddForm(prev => ({ ...prev, city: e.target.value }))}
              size="small"
              required={isShopify}
              className="flex-1"
            />
          </Box>
          <Box className="form-row-2col">
            <TextField
              label={t("locationSetup.columns.address2")}
              value={addForm.address2}
              onChange={(e) => setAddForm(prev => ({ ...prev, address2: e.target.value }))}
              size="small"
              className="flex-1"
            />
            <TextField
              label={t("locationSetup.columns.postalCode")}
              value={addForm.postalCode}
              onChange={(e) => setAddForm(prev => ({ ...prev, postalCode: e.target.value }))}
              size="small"
              required={isShopify}
              className="flex-1"
            />
          </Box>
          <Box className="form-row-2col">
            <TextField
              label={t("locationSetup.columns.stateProvince")}
              value={addForm.stateProvince}
              onChange={(e) => setAddForm(prev => ({ ...prev, stateProvince: e.target.value }))}
              size="small"
              className="flex-1"
            />
            <Autocomplete
              options={countries}
              value={countries.find(c => c.country_code === addForm.country) || null}
              getOptionLabel={(o) => o.country_name || ''}
              onChange={(_, v) => setAddForm(prev => ({ ...prev, country: v?.country_code || null }))}
              renderInput={(params) => <TextField {...params} label={t("locationSetup.columns.country")} size="small" required={isShopify} />}
              size="small"
              className="flex-1"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setAddDialogOpen(false)}>{t("btn.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleAddLocation}
            disabled={!addForm.name.trim() || !addForm.calendar || addLengths.tooLong || (isShopify && (!addForm.address1.trim() || !addForm.city.trim() || !addForm.postalCode.trim() || !addForm.country))}
            loading={addLoading}
          >
            {t("locationSetup.addLocation")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Location Dialog ──
          Address is required only on Shopify stores (parity with Add).
          Non-Shopify stores can leave address fields blank. */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} className="dialog-form">
        <DialogTitle>
          {t("locationSetup.editLocation")}
          <IconButton onClick={() => setEditDialogOpen(false)} size="small"><X size={ICON.xl} /></IconButton>
        </DialogTitle>
        <DialogContent>
          {isShopify && (
            <Alert severity="info">
              {t("locationSetup.messages.shopifyAddressInfo")}
            </Alert>
          )}

          {/* Section: General */}
          <Typography className="dialog-form-section">{t("locationSetup.sections.general")}</Typography>
          <TextField
            label={t("locationSetup.columns.name")}
            value={editForm.name}
            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
            size="small"
          />

          {/* Section: Address */}
          <Typography className="dialog-form-section">{t("locationSetup.sections.address")}</Typography>
          <TextField
            label={t("locationSetup.columns.address1")}
            value={editForm.address1}
            onChange={(e) => setEditForm(prev => ({ ...prev, address1: e.target.value }))}
            size="small"
            required={isShopify}
            error={isShopify && !editForm.address1.trim()}
          />
          <TextField
            label={t("locationSetup.columns.address2")}
            value={editForm.address2}
            onChange={(e) => setEditForm(prev => ({ ...prev, address2: e.target.value }))}
            size="small"
          />
          <Box className="form-row-2col">
            <TextField
              label={t("locationSetup.columns.city")}
              value={editForm.city}
              onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
              size="small"
              required={isShopify}
              error={isShopify && !editForm.city.trim()}
              className="flex-1"
            />
            <TextField
              label={t("locationSetup.columns.postalCode")}
              value={editForm.postalCode}
              onChange={(e) => setEditForm(prev => ({ ...prev, postalCode: e.target.value }))}
              size="small"
              required={isShopify}
              error={isShopify && !editForm.postalCode.trim()}
              className="flex-half"
            />
          </Box>
          <Box className="form-row-2col">
            <TextField
              label={t("locationSetup.columns.stateProvince")}
              value={editForm.stateProvince}
              onChange={(e) => setEditForm(prev => ({ ...prev, stateProvince: e.target.value }))}
              size="small"
              className="flex-1"
            />
            <Autocomplete
              options={countries}
              value={countries.find(c => c.country_code === editForm.country) || null}
              getOptionLabel={(o) => o.country_name || ''}
              onChange={(_, v) => setEditForm(prev => ({ ...prev, country: v?.country_code || null }))}
              renderInput={(params) => <TextField {...params} label={t("locationSetup.columns.country")} size="small" required={isShopify} />}
              size="small"
              className="flex-1"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditDialogOpen(false)}>{t("btn.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleEditLocation}
            disabled={isShopify && (!editForm.address1.trim() || !editForm.city.trim() || !editForm.postalCode.trim() || !editForm.country)}
            loading={editLoading}
          >
            {t("btn.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Calendar Modal ── */}
      <Dialog
        open={calendarModalOpen}
        onClose={closeCalendarModal}
        className="dialog-form dialog-form--confirm"
      >
        <DialogTitle className="calendar-modal-header">
          <CalendarBlank className="calendar-modal-icon" size={ICON.xxl} />
          <div className="calendar-modal-header-content">
            {modalCalendar ? (
              <Tooltip title={t("locationSetup.calendar.editNameHint")} describeChild>
                <button
                  type="button"
                  className="calendar-modal-name-display"
                  onClick={() => calendarEditorRef.current?.focusName()}
                >
                  <span className="calendar-modal-name-text">
                    {modalCalendarName || t("locationSetup.calendar.namePlaceholder")}
                  </span>
                  <PencilSimple
                    size={ICON.md}
                    weight="regular"
                    className="calendar-modal-name-pencil"
                    aria-label={t("locationSetup.calendar.editNameHint")}
                  />
                </button>
              </Tooltip>
            ) : (
              <div>
                {t("locationSetup.calendar.noCalendar")}
              </div>
            )}
            {modalCalendar && (
              <div className="dialog-form-subtitle">
                {t("locationSetup.calendar.assignedCount", {
                  count: allLocations.filter(d => d.calendar?.calendar_id === modalCalendar.calendar_id).length
                })}
              </div>
            )}
          </div>
          <IconButton size="small" onClick={closeCalendarModal} aria-label={t("btn.close")}>
            <X size={ICON.xl} />
          </IconButton>
        </DialogTitle>

        <DialogContent className="calendar-modal-content">
          {/* Calendar selector — always visible */}
          <div>
            <Autocomplete
              options={[
                { calendar_id: '__NEW__', calendar_name: t("locationSetup.calendar.createNew") },
                ...calendars,
              ]}
              value={modalCalendar ? (calendars.find(c => c.calendar_id === modalCalendar.calendar_id) || null) : null}
              getOptionLabel={(option) => option.calendar_name || ''}
              isOptionEqualToValue={(option, value) => option.calendar_id === value.calendar_id}
              onChange={(_, value) => {
                if (!value) return;
                if (value.calendar_id === '__NEW__') {
                  handleCreateCalendar();
                } else if (!modalCalendar || value.calendar_id !== modalCalendar.calendar_id) {
                  handleSelectCalendar(value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("locationSetup.calendar.assignCalendar")}
                  label={t("locationSetup.calendar.assignCalendar")}
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.calendar_id}>
                  {option.calendar_id === '__NEW__' ? (
                    <span style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)' }}>
                      + {option.calendar_name}
                    </span>
                  ) : (
                    option.calendar_name
                  )}
                </li>
              )}
              size="small"
              fullWidth
              disableClearable
            />
          </div>

          {modalCalendar && (
            <>
              {/* Working Days + Delivery Rule */}
              <div ref={workingDaysSectionRef}>
                <CalendarEditor
                  ref={calendarEditorRef}
                  calendar={modalCalendar}
                  onUpdate={handleCalendarUpdate}
                  onStatusChange={setCalendarStatus}
                  onPatternChange={setPreviewPattern}
                  onNameChange={setModalCalendarName}
                />
              </div>

              {/* Recurring Holidays */}
              <div ref={holidaysSectionRef}>
                <div className="calendar-modal-section-title">
                  {t("calendar.holidays.sectionTitle")}
                </div>
                <CalendarHolidaysSection
                  calendar={modalCalendar}
                />
              </div>

              {/* One-off Exceptions */}
              <div>
                <div className="calendar-modal-section-title">
                  {t("calendar.exceptions.sectionTitle")}
                </div>
                <CalendarExceptionsSection
                  calendar={modalCalendar}
                />
              </div>

              {/* Calendar View */}
              <div>
                <div className="calendar-modal-section-title">
                  {t("locationSetup.calendar.calendarView")}
                </div>
                <CalendarDaysView
                  calendar={modalCalendar}
                  workingDaysPattern={previewPattern}
                  onScrollToHolidays={() => holidaysSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  onScrollToWorkingDays={() => workingDaysSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                />
              </div>

              {/* Locations */}
              <div>
                <div className="calendar-modal-section-title">
                  {t("locationSetup.calendar.locations")}
                </div>
                {allLocations.length > 0 ? (
                  <ul className="calendar-modal-assigned-list">
                    {allLocations.map((loc) => {
                      const isAssigned = loc.calendar?.calendar_id === modalCalendar.calendar_id;
                      return (
                        <li key={loc.location_id} className="calendar-modal-assigned-item">
                          <Checkbox
                            checked={isAssigned}
                            onChange={() => handleToggleLocation(loc.location_id, isAssigned)}
                            size="small"
                            sx={{ p: 0 }}
                          />
                          {formatLocationLabel(loc, isShopify)}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="calendar-modal-no-assigned">
                    {t("locationSetup.calendar.noAssigned")}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={closeCalendarModal}>
            {t("btn.close")}
          </Button>
          {modalCalendar && (
            <>
              <Button
                variant="outlined"
                onClick={() => calendarEditorRef.current?.discard()}
                disabled={!calendarStatus.dirty || updateCalendarMutation.isPending}
              >
                {t("btn.discardChanges")}
              </Button>
              <Button
                variant="contained"
                onClick={() => calendarEditorRef.current?.save()}
                disabled={!calendarStatus.canSave}
                loading={updateCalendarMutation.isPending}
              >
                {t("btn.saveChanges")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}

export default LocationSetup;
